import { usePageMeta } from '../lib/usePageMeta';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AppStoreButton } from '../components/shared/AppStoreButton';
import { PhoneFrame } from '../components/shared/PhoneFrame';

/* ------------------------------------------------------------------------ */
/* Pieces                                                                   */
/* ------------------------------------------------------------------------ */

type Zone = { min: number; label: string; color: string; badge: string; advice: string };

// Same depth bands as the app's report (backend/models/config.py); 1.6 mm (2/32″) is the US legal minimum.
const ZONES: Zone[] = [
  { min: 4, label: 'Good', color: '#2E7D32', badge: 'bg-good-bg text-good-fg', advice: 'Plenty of grip left. Check again in a few months.' },
  { min: 2.5, label: 'Replace soon', color: '#E8833A', badge: 'bg-[#FFE2CC] text-[#8A3F06]', advice: 'Fine in the dry, weaker in rain and snow. Start pricing new tires.' },
  { min: 1.6, label: 'Replace', color: '#D93A2E', badge: 'bg-replace-bg text-replace-fg', advice: 'Wet-road braking is much longer now. Replace these tires.' },
  { min: 0, label: 'Below legal limit', color: '#8C1D18', badge: 'bg-replace-bg text-replace-fg', advice: 'Under 2/32″, the legal minimum in most US states. Replace before driving far.' },
];

const MIN = 0.5;
const MAX = 8;
const at = (mm: number) => ((mm - MIN) / (MAX - MIN)) * 100;
// Hard-stop gradient so the slider track itself shows the bands.
const TRACK = `linear-gradient(90deg, #8C1D18 0 ${at(1.6)}%, #D93A2E ${at(1.6)}% ${at(2.5)}%, #E8833A ${at(2.5)}% ${at(4)}%, #2E7D32 ${at(4)}% 100%)`;

/** Drag the depth and watch the tread blocks wear down to the wear bars. */
const TreadRuler = () => {
  const [mm, setMm] = useState(4.6);
  const zone = ZONES.find((z) => mm >= z.min)!;
  // Round 32nds down: 1.2 mm should read 1/32″, not 2/32″ (the legal minimum).
  const thirtySeconds = Math.floor((mm / 25.4) * 32 + 1e-6);

  // Cross-section geometry (SVG units): 10 blocks on a carcass, wear bars in the grooves.
  const W = 440, BASE = 118, FULL = 100;
  const h = (d: number) => (d / MAX) * FULL;
  const blocks = 8, groove = 14, bw = (W - groove * (blocks - 1)) / blocks;
  const atLimit = mm <= 1.6;

  // iPhone Safari only moves a range input when the thumb itself is dragged;
  // jump to wherever the track is touched, then let the native drag continue.
  const jump = (e: React.PointerEvent<HTMLInputElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const thumb = 30;
    const f = Math.min(1, Math.max(0, (e.clientX - r.left - thumb / 2) / (r.width - thumb)));
    setMm(Math.round((MIN + f * (MAX - MIN)) * 10) / 10);
  };

  return (
    <section className="bg-rubber text-white overflow-hidden" aria-labelledby="ruler-title">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 md:py-24 grid gap-10 md:gap-12 md:grid-cols-[1fr_1.15fr] items-center">
        <div>
          <h2 id="ruler-title" className="text-[2rem] md:text-[2.6rem] leading-[1.08] font-semibold tracking-tight" style={{ fontVariationSettings: '"wdth" 110' }}>
            How much tread is enough?
          </h2>
          <p className="mt-4 text-white/70 text-[17px] md:text-lg leading-relaxed max-w-md">
            New tires start around 8 mm. Drag the slider to see what each depth means, using the same bands Tread AI puts on
            your report.
          </p>

          <div className="mt-8 hidden md:block" aria-live="polite">
            <Readout mm={mm} n32={thirtySeconds} zone={zone} />
          </div>
        </div>

        <div className="rounded-[1.75rem] bg-white/[0.04] ring-1 ring-white/10 p-5 sm:p-7">
          {/* On phones the readout sits right above what you're dragging */}
          <div className="md:hidden mb-6" aria-live="polite">
            <Readout mm={mm} n32={thirtySeconds} zone={zone} compact />
          </div>

          <svg viewBox={`0 0 ${W} ${BASE + 22}`} className="w-full h-auto" aria-hidden="true">
            <rect x="0" y={BASE} width={W} height="22" rx="8" fill="#2C2D32" />
            {Array.from({ length: blocks }, (_, i) => {
              const x = i * (bw + groove);
              return (
                <g key={i}>
                  <rect
                    x={x}
                    y={BASE - h(mm)}
                    width={bw}
                    height={h(mm) + 2}
                    rx="7"
                    fill={atLimit ? '#5B3A39' : '#3D3E45'}
                    style={{ transition: 'y .15s ease, height .15s ease, fill .2s ease' }}
                  />
                  {i < blocks - 1 && (
                    <rect x={x + bw + 3} y={BASE - h(1.6)} width={groove - 6} height={h(1.6)} rx="2" fill="#5A5C66" />
                  )}
                </g>
              );
            })}
          </svg>
          <p className="mt-3 text-xs text-white/45 leading-relaxed">
            The small bars in the grooves are wear bars, molded in at 2/32″. When the tread is flush with them, the tire is at the legal limit.
          </p>

          <label htmlFor="depth" className="sr-only">Tread depth in millimetres</label>
          <input
            id="depth"
            type="range"
            min={MIN}
            max={MAX}
            step={0.1}
            value={mm}
            onChange={(e) => setMm(parseFloat(e.target.value))}
            onPointerDown={jump}
            className="tread-range mt-5"
            style={{ ['--track' as string]: TRACK, ['--thumb' as string]: zone.color }}
            aria-valuetext={`${mm.toFixed(1)} millimetres, ${zone.label}`}
          />
          <div className="relative h-4 text-xs text-white/50 tabular" aria-hidden="true">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <span key={n} className="absolute -translate-x-1/2" style={{ left: `calc(15px + (100% - 30px) * ${(n - MIN) / (MAX - MIN)})` }}>
                {n}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Readout = ({ mm, n32, zone, compact }: { mm: number; n32: number; zone: Zone; compact?: boolean }) => (
  <div>
    <div className="flex items-baseline gap-3">
      <span className={`${compact ? 'text-5xl' : 'text-6xl md:text-7xl'} font-semibold tabular tracking-tight`}>
        {mm.toFixed(1)}
        <span className={`${compact ? 'text-xl' : 'text-2xl md:text-3xl'} text-white/60 font-normal ml-1`}>mm</span>
      </span>
      <span className="text-white/60 tabular">{n32}/32″</span>
      {compact && <span className={`ml-auto shrink-0 px-3 py-1 rounded-full text-sm font-medium ${zone.badge}`}>{zone.label}</span>}
    </div>
    <div className={`mt-3 flex items-start gap-3 ${compact ? 'min-h-[3rem]' : 'min-h-[4.5rem]'}`}>
      {!compact && <span className={`shrink-0 mt-0.5 px-3 py-1 rounded-full text-sm font-medium ${zone.badge}`}>{zone.label}</span>}
      <p className="text-white/85 leading-relaxed">{zone.advice}</p>
    </div>
  </div>
);

const STEPS = [
  {
    title: 'Tell it which car',
    body: 'Type your license plate or scan it with the camera. Make, model and tire size are optional, but they sharpen the reading.',
    img: '/app/2-vehicle.png',
    alt: 'Vehicle identification screen with license plate, make, model and tire details',
  },
  {
    title: 'Photograph each tire',
    body: 'Two shots per tire: the sidewall, where Tread AI reads the brand, size and DOT date code, and the tread, where it measures depth. A frame on screen shows where to aim.',
    img: '/app/3-four-tires.png',
    alt: 'Four-tire screen showing photos and a depth reading for each tire',
  },
  {
    title: 'Read the report',
    body: 'Depth, age and wear pattern for every tire, with a plain-language recommendation. Save it as a PDF or send it to your shop.',
    img: '/app/4-report.png',
    alt: 'Inspection report with per-tire depth, age, DOT code and wear pattern',
  },
];

const FAQ = [
  {
    q: 'How accurate is it?',
    a: 'On our test set of real tires, readings are off by about 1.4 mm on average, and closer in the 4.5–6 mm range where most replacement decisions happen. That is enough to tell a healthy tire from a worn one, but not a substitute for a tread gauge when you are near the limit.',
  },
  {
    q: 'What does it cost?',
    a: 'Your first inspection is free. Unlimited inspections come with Tread AI Premium; the current price is shown in the App Store before you subscribe, and you can cancel any time in your Apple ID settings.',
  },
  {
    q: 'How does it know how old my tires are?',
    a: 'From the DOT code on the sidewall. The last four digits are the week and year the tire was made, so 3823 means week 38 of 2023. Rubber hardens with age, so tires older than about six years deserve a closer look even with good tread.',
  },
  {
    q: 'Do I need an iPhone?',
    a: 'The full guided inspection is in the iPhone app. You can also upload photos and get a reading in your browser with Check tires online.',
  },
  {
    q: 'What happens to my photos?',
    a: 'They are used to produce your report and stored with your inspection. Details are in the privacy policy.',
  },
];

/* ------------------------------------------------------------------------ */
/* Page                                                                     */
/* ------------------------------------------------------------------------ */

export const Dashboard = () => {
  usePageMeta(null, "Check your tire tread depth, tire age and wear with your iPhone. Tread AI reads all four tires from photos and gives you a shareable report.");
  return (
  <div>
    {/* Hero */}
    <section className="relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 md:pt-20 pb-16 md:pb-24 grid gap-12 md:grid-cols-[1.05fr_1fr] items-center">
        <div>
          <h1
            className="text-[2.6rem] sm:text-5xl md:text-[3.75rem] leading-[1.03] font-semibold tracking-[-0.02em] text-ink"
            style={{ fontVariationSettings: '"wdth" 112' }}
          >
            Know if your tires are safe, from eight photos.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-muted leading-relaxed max-w-xl">
            Tread AI measures tread depth, reads the tire's age off the sidewall, and tells you which tires need attention,
            in about five minutes with your iPhone.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <AppStoreButton placement="home_hero" />
            <Link
              to="/inspect"
              className="h-14 inline-flex items-center px-6 rounded-full border border-line text-ink font-medium hover:bg-surface transition-colors"
            >
              Check tires online
            </Link>
          </div>
          <p className="mt-5 text-sm text-muted">First inspection free. Works on iPhone with iOS 13.4 or later.</p>
        </div>

        <div className="relative h-[460px] sm:h-[560px] md:h-[600px]" aria-label="Tread AI app screens">
          <div className="absolute left-[6%] top-[8%] w-[46%] max-w-[250px] opacity-95">
            <PhoneFrame src="/app/1-home.png" alt="Tread AI home screen with a tire button labeled Tap to start" />
          </div>
          <div className="absolute right-[4%] top-0 w-[52%] max-w-[280px]">
            <PhoneFrame src="/app/4-report.png" alt="Inspection report showing 6.9 mm and 8.1 mm readings rated Good" />
          </div>
        </div>
      </div>
    </section>

    <TreadRuler />

    {/* Steps */}
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20 md:py-28" aria-labelledby="steps-title">
      <h2 id="steps-title" className="text-3xl md:text-[2.6rem] leading-[1.1] font-semibold tracking-tight max-w-2xl" style={{ fontVariationSettings: '"wdth" 110' }}>
        One inspection, start to finish
      </h2>
      <ol className="mt-14 space-y-20 md:space-y-28">
        {STEPS.map((s, i) => (
          <li key={s.title} className={`grid gap-10 md:grid-cols-2 items-center ${i % 2 ? 'md:[&>div:first-child]:order-2' : ''}`}>
            <div className="max-w-md">
              <span className="inline-flex w-9 h-9 rounded-full bg-brand-600 text-white items-center justify-center font-semibold tabular" aria-hidden="true">
                {i + 1}
              </span>
              <h3 className="mt-4 text-2xl md:text-3xl font-semibold tracking-tight">{s.title}</h3>
              <p className="mt-3 text-lg text-muted leading-relaxed">{s.body}</p>
            </div>
            <div className="flex justify-center">
              <PhoneFrame src={s.img} alt={s.alt} className="w-[62%] max-w-[290px]" />
            </div>
          </li>
        ))}
      </ol>
    </section>

    {/* Browser option */}
    <section className="bg-brand-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="max-w-xl">
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-brand-900">No iPhone handy?</h2>
          <p className="mt-2 text-brand-900/80 text-lg leading-relaxed">
            Upload tread photos from any computer or phone and get the same depth reading in your browser.
          </p>
        </div>
        <Link
          to="/inspect"
          className="h-14 inline-flex items-center justify-center px-7 rounded-full bg-brand-600 text-white font-medium hover:bg-brand-700 transition-colors shrink-0"
        >
          Check tires online
        </Link>
      </div>
    </section>

    {/* FAQ */}
    <section className="max-w-3xl mx-auto px-4 sm:px-6 py-20 md:py-28" aria-labelledby="faq-title">
      <h2 id="faq-title" className="text-3xl md:text-[2.6rem] leading-[1.1] font-semibold tracking-tight" style={{ fontVariationSettings: '"wdth" 110' }}>
        Questions
      </h2>
      <div className="mt-10 divide-y divide-line/70 border-y border-line/70">
        {FAQ.map((f) => (
          <details key={f.q} className="group py-5">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-lg font-medium text-ink">
              {f.q}
              <svg className="w-5 h-5 shrink-0 text-muted transition-transform group-open:rotate-45" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </summary>
            <p className="mt-3 text-muted leading-relaxed max-w-[65ch]">{f.a}</p>
          </details>
        ))}
      </div>
      <p className="mt-8 text-muted">
        Something else? Email{' '}
        <a href="mailto:contact@tread-ai.com" className="text-brand-700 underline underline-offset-2">contact@tread-ai.com</a> or{' '}
        <a href="mailto:sizarta@gmail.com" className="text-brand-700 underline underline-offset-2">sizarta@gmail.com</a>,{' '}
        or call <a href="tel:+19495584019" className="text-brand-700 underline underline-offset-2 tabular">(949) 558-4019</a>.
      </p>
    </section>

    {/* Closing CTA */}
    <section className="border-t border-line/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-20 flex flex-col md:flex-row md:items-center justify-between gap-8">
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight max-w-xl" style={{ fontVariationSettings: '"wdth" 110' }}>
          Check all four tires before your next long drive.
        </h2>
        <AppStoreButton placement="home_closing" />
      </div>
    </section>
  </div>
);
};
